import { createContext, useState, useEffect, useCallback, useRef, useContext } from 'react'
import api, { setAccessToken as setAxiosAccessToken, requestRefresh, setRefreshFailureHandler, setAuthErrorHandler, resetRefreshCooldown } from '../api/axios'

export const AuthContext = createContext(null)

const STORAGE_KEY_ACCOUNTS = 'stored_accounts'
const STORAGE_KEY_ACTIVE_INDEX = 'active_account_index'

const loadStoredAccounts = () => {
  let accs = []
  try {
    const raw = localStorage.getItem(STORAGE_KEY_ACCOUNTS)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        accs = parsed
      }
    }
  } catch (_) {
    accs = []
  }

  // Ensure any standalone tokens in localStorage are logically synchronized into stored_accounts
  try {
    const singleToken = localStorage.getItem('access_token')
    const singleRefresh = localStorage.getItem('refresh_token')
    const singleUserRaw = localStorage.getItem('user')

    if (singleToken || singleRefresh || singleUserRaw) {
      let singleUser = null
      try {
        singleUser = singleUserRaw ? JSON.parse(singleUserRaw) : null
      } catch (_) {}

      if (singleToken || singleUser) {
        const existingIdx = accs.findIndex(
          (a) =>
            (singleUser?.sso_id && a.user?.sso_id === singleUser.sso_id) ||
            (singleUser?.id && a.user?.id === singleUser.id) ||
            (singleUser?.email && a.user?.email === singleUser.email) ||
            (singleUser?.username && a.user?.username === singleUser.username) ||
            (singleToken && a.access_token === singleToken)
        )

        const standaloneAcc = {
          id: singleUser?.sso_id || singleUser?.id || (singleUser?.email ? `email_${singleUser.email}` : Date.now()),
          user: singleUser,
          access_token: singleToken,
          refresh_token: singleRefresh,
        }

        let updated = false
        let activeIdx = null

        if (existingIdx >= 0) {
          if (
            accs[existingIdx].access_token !== singleToken ||
            accs[existingIdx].refresh_token !== singleRefresh ||
            JSON.stringify(accs[existingIdx].user) !== JSON.stringify(singleUser)
          ) {
            accs[existingIdx] = {
              ...accs[existingIdx],
              ...standaloneAcc,
              user: singleUser || accs[existingIdx].user,
              access_token: singleToken || accs[existingIdx].access_token,
              refresh_token: singleRefresh || accs[existingIdx].refresh_token,
            }
            updated = true
          }
          activeIdx = existingIdx
        } else {
          // If standalone tokens belong to a new/different account, append it
          accs.push(standaloneAcc)
          activeIdx = accs.length - 1
          updated = true
        }

        if (updated) {
          localStorage.setItem(STORAGE_KEY_ACCOUNTS, JSON.stringify(accs))
          if (activeIdx !== null) {
            localStorage.setItem(STORAGE_KEY_ACTIVE_INDEX, String(activeIdx))
          }
        }
      }
    }
  } catch (_) {}

  return accs
}

const getStoredActiveIndex = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_ACTIVE_INDEX)
    const parsed = raw !== null ? parseInt(raw, 10) : null
    if (!isNaN(parsed) && parsed !== null) {
      return parsed
    }
  } catch (_) {}

  try {
    const accs = loadStoredAccounts()
    if (accs.length > 0) {
      localStorage.setItem(STORAGE_KEY_ACTIVE_INDEX, '0')
      return 0
    }
  } catch (_) {}

  return null
}

/**
 * AuthProvider (Pure JWT Architecture with Multi-Account Support)
 * - Supports multiple logged-in accounts stored in localStorage.
 * - The first logged-in account becomes the default active account.
 * - Selecting an account sets it as the new default active account.
 * - Logging out single account revokes & removes ONLY that selected account.
 * - Provides logoutAll() to log out all accounts simultaneously.
 */
export function AuthProvider({ children }) {
  const isInitializingRef = useRef(false)
  // Holds the React Router navigate() function, registered by a component inside the router tree
  const navigateRef = useRef(null)

  const [accounts, setAccounts] = useState(() => loadStoredAccounts())
  const [activeIndex, setActiveIndex] = useState(() => getStoredActiveIndex())

  const [user, setUser] = useState(() => {
    try {
      const u = localStorage.getItem('user')
      return u ? JSON.parse(u) : null
    } catch (_) {
      return null
    }
  })
  const [accessToken, setAccessTokenState] = useState(() => localStorage.getItem('access_token'))
  const [refreshToken, setRefreshTokenState] = useState(() => localStorage.getItem('refresh_token'))
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(() => {
    try {
      const singleToken = localStorage.getItem('access_token')
      const rawAccounts = localStorage.getItem(STORAGE_KEY_ACCOUNTS)
      const parsed = rawAccounts ? JSON.parse(rawAccounts) : []
      return !!(singleToken || (Array.isArray(parsed) && parsed.length > 0))
    } catch (_) {
      return false
    }
  })
  const [sessionExpired, setSessionExpired] = useState(false)
  const [showLoginPopupModal, setShowLoginPopupModal] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [authError, setAuthError] = useState(null)
  const [isSessionReady, setIsSessionReady] = useState(false)

  useEffect(() => {
    setAuthErrorHandler((err, source) => {
      const status = err?.response?.status
      const errorMsg =
        err?.response?.data?.message ||
        (err?.message === 'Network Error' ? 'Unable to reach the server. Please check your connection.' : err?.message) ||
        'Authentication session error.'

      setAuthError({
        title: status === 503 ? 'Database Service Unavailable' : 'Authentication Session Error',
        message: errorMsg,
        details: err?.response?.data?.error || (err?.code ? `Error code: ${err.code}` : null),
        status: status,
        retryFn: () => {
          resetRefreshCooldown()
          setAuthError(null)
          return requestRefresh()
        },
      })
    })
  }, [])

  // Persists account list and active index into localStorage, mirroring active tokens
  const saveStoredAccounts = useCallback((accs, activeIdx) => {
    try {
      localStorage.setItem(STORAGE_KEY_ACCOUNTS, JSON.stringify(accs))
      if (activeIdx !== null && activeIdx !== undefined && activeIdx >= 0 && activeIdx < accs.length) {
        localStorage.setItem(STORAGE_KEY_ACTIVE_INDEX, String(activeIdx))
        const activeAcc = accs[activeIdx]
        if (activeAcc?.access_token) {
          localStorage.setItem('access_token', activeAcc.access_token)
          setAxiosAccessToken(activeAcc.access_token)
          setAccessTokenState(activeAcc.access_token)
        }
        if (activeAcc?.refresh_token) {
          localStorage.setItem('refresh_token', activeAcc.refresh_token)
          setRefreshTokenState(activeAcc.refresh_token)
        }
        if (activeAcc?.user) {
          localStorage.setItem('user', JSON.stringify(activeAcc.user))
          setUser(activeAcc.user)
        }
      } else {
        localStorage.removeItem(STORAGE_KEY_ACTIVE_INDEX)
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user')
        setAxiosAccessToken(null)
        setAccessTokenState(null)
        setRefreshTokenState(null)
        setUser(null)
      }
    } catch (_) {}
  }, [])

  // Adds or updates an account in multi-account storage and optionally makes it active
  const addOrUpdateAccount = useCallback((token, refreshTok, userData, setAsActive = true) => {
    let currentAccounts = loadStoredAccounts()

    // Match by user sso_id, id, email, or username
    const existingIndex = currentAccounts.findIndex(
      (a) =>
        (userData?.sso_id && a.user?.sso_id === userData.sso_id) ||
        (userData?.id && a.user?.id === userData.id) ||
        (userData?.email && a.user?.email === userData.email) ||
        (userData?.username && a.user?.username === userData.username)
    )

    const newAcc = {
      id: userData?.sso_id || userData?.id || (userData?.email ? `email_${userData.email}` : Date.now()),
      user: userData,
      access_token: token,
      refresh_token: refreshTok,
    }

    let nextActiveIndex = getStoredActiveIndex()

    if (existingIndex >= 0) {
      currentAccounts[existingIndex] = newAcc
      if (setAsActive || nextActiveIndex === null) {
        nextActiveIndex = existingIndex
      }
    } else {
      currentAccounts.push(newAcc)
      if (setAsActive || currentAccounts.length === 1 || nextActiveIndex === null) {
        nextActiveIndex = currentAccounts.length - 1
      }
    }

    if (nextActiveIndex === null || nextActiveIndex < 0 || nextActiveIndex >= currentAccounts.length) {
      nextActiveIndex = currentAccounts.length > 0 ? 0 : null
    }

    setAccounts([...currentAccounts])
    setActiveIndex(nextActiveIndex)
    saveStoredAccounts(currentAccounts, nextActiveIndex)
  }, [saveStoredAccounts])

  // Backward compatible setSession helper
  const setSession = useCallback((token, refreshTok, userData) => {
    if (!token && !userData) {
      // Clear current session
      saveStoredAccounts([], null)
      setAccounts([])
      setActiveIndex(null)
    } else {
      addOrUpdateAccount(token, refreshTok, userData, true)
    }
  }, [addOrUpdateAccount, saveStoredAccounts])

  // Handles refresh failures at axios layer
  useEffect(() => {
    setRefreshFailureHandler(() => {
      // Do NOT wipe accounts from memory so active workspace and in-progress form data are preserved
      setSessionExpired(true)
      setShowLoginPopupModal(true)
    })
  }, [])

  // Synchronize accounts state across tabs/windows when localStorage changes
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === STORAGE_KEY_ACCOUNTS && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue)
          if (Array.isArray(parsed)) {
            setAccounts([...parsed])
          }
        } catch (_) {}
      }
      if (e.key === STORAGE_KEY_ACTIVE_INDEX && e.newValue !== null) {
        const parsedIdx = parseInt(e.newValue, 10)
        if (!isNaN(parsedIdx)) {
          setActiveIndex(parsedIdx)
        }
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  // Global postMessage listener for popup authentication across the entire app
  useEffect(() => {
    const handler = async (event) => {
      const allowedTypes = ['OAUTH_AUTH_SUCCESS', 'LOGIN_SUCCESS', 'ONE_LOGIN_AUTH_SUCCESS', 'login_success']
      const data = event?.data || {}

      const isSuccess = data?.success === true || allowedTypes.includes(data?.type)
      if (!isSuccess) {
        return
      }

      console.log('Auth postMessage success received in parent window. Fetching user data via /me...')

      try {
        // Read token from payload or localStorage fallback
        const payload = data.payload || data || {}
        const token = payload.accessToken || payload.token || payload.access_token || localStorage.getItem('access_token') || null
        const refreshTok = payload.refreshToken || payload.refresh_token || localStorage.getItem('refresh_token') || null

        if (token) {
          setAxiosAccessToken(token)
        }

        // Call /me API immediately to fetch fresh profile
        let freshUser = null
        try {
          const res = await api.get('/users/me', {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          })
          freshUser = res?.data?.user || res?.data
        } catch (_) {
          freshUser = payload.user || null
        }

        if (!freshUser && payload.user) {
          freshUser = payload.user
        }

        if (freshUser) {
          addOrUpdateAccount(token, refreshTok, freshUser, true)
          setSessionExpired(false)
          setShowLoginPopupModal(false)
          setLoading(false)
          setIsSessionReady(true)

          // Determine which account index was just set so the URL is correct
          const allAccounts = loadStoredAccounts()
          const newIdx = allAccounts.findIndex(
            (a) =>
              (freshUser?.sso_id && a.user?.sso_id === freshUser.sso_id) ||
              (freshUser?.id && a.user?.id === freshUser.id) ||
              (freshUser?.email && a.user?.email === freshUser.email) ||
              (freshUser?.username && a.user?.username === freshUser.username)
          )
          const targetIdx = newIdx >= 0 ? newIdx : 0

          // If user was already on a dashboard route, preserve the current subpath!
          const currentPath = window.location.pathname
          const isDashboardRoute = /^\/u\/\d+\/dashboard/.test(currentPath)
          const targetPath = isDashboardRoute
            ? currentPath.replace(/^\/u\/\d+/, `/u/${targetIdx}`)
            : `/u/${targetIdx}/dashboard/overview`

          if (navigateRef.current) {
            navigateRef.current(targetPath, { replace: true })
          } else {
            // Fallback only if navigate hasn't been registered yet
            window.location.href = targetPath
          }
        }
      } catch (err) {
        console.error('Failed to fetch user profile after popup success:', err)
      }
    }

    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [addOrUpdateAccount])

  // On app load / reload initialization (guaranteed single run)
  useEffect(() => {
    // If running inside a popup authentication window with an opener, synchronize local storage and initialize state without full blocking network /users/me verification
    if (
      typeof window !== 'undefined' &&
      Boolean(window.opener) &&
      (window.location.pathname.startsWith('/auth/popup') || window.location.pathname.startsWith('/auth/select-account'))
    ) {
      const storedAccs = loadStoredAccounts()
      const activeIdx = getStoredActiveIndex()
      if (storedAccs.length > 0) {
        setAccounts([...storedAccs])
        const idx = activeIdx !== null && activeIdx >= 0 && activeIdx < storedAccs.length ? activeIdx : 0
        setActiveIndex(idx)
        const activeAcc = storedAccs[idx]
        if (activeAcc?.access_token) {
          setAxiosAccessToken(activeAcc.access_token)
          setAccessTokenState(activeAcc.access_token)
        }
        if (activeAcc?.refresh_token) {
          setRefreshTokenState(activeAcc.refresh_token)
        }
        if (activeAcc?.user) {
          setUser(activeAcc.user)
        }
      }
      setLoading(false)
      setIsSessionReady(true)
      return
    }

    if (isInitializingRef.current) return
    isInitializingRef.current = true

    let cancelled = false
    const safetyTimer = setTimeout(() => {
      setLoading(false)
    }, 5000)

    const initialize = async () => {
      try {
        let storedAccs = loadStoredAccounts()
        let activeIdx = getStoredActiveIndex()

        // Fallback migration: if single token exists in localStorage but no stored_accounts array
        const singleToken = localStorage.getItem('access_token')
        const singleRefresh = localStorage.getItem('refresh_token')
        const singleUserRaw = localStorage.getItem('user')

        if (storedAccs.length === 0 && (singleToken || singleRefresh)) {
          let singleUser = null
          try { singleUser = singleUserRaw ? JSON.parse(singleUserRaw) : null } catch (_) {}
          if (singleUser || singleToken) {
            storedAccs = [{
              id: singleUser?.id || singleUser?.sso_id || 1,
              user: singleUser,
              access_token: singleToken,
              refresh_token: singleRefresh,
            }]
            activeIdx = 0
            saveStoredAccounts(storedAccs, 0)
          }
        }

        if (storedAccs.length === 0) {
          saveStoredAccounts([], null)
          setAccounts([])
          setActiveIndex(null)
          return
        }

        if (activeIdx === null || activeIdx < 0 || activeIdx >= storedAccs.length) {
          activeIdx = 0
        }

        setAccounts(storedAccs)
        setActiveIndex(activeIdx)

        const activeAcc = storedAccs[activeIdx]
        const initialToken = localStorage.getItem('access_token') || activeAcc?.access_token
        if (initialToken) {
          setAxiosAccessToken(initialToken)
        }

        try {
          setIsSessionReady(false)
          // Axios response interceptor automatically handles 401 by calling /auth/refresh and retrying
          const profileRes = await api.get('/users/me', {
            headers: initialToken ? { Authorization: `Bearer ${initialToken}` } : {},
          })

          const fetchedUser = profileRes?.data?.user || null
          if (fetchedUser) {
            // Re-read latest tokens that may have just been refreshed by axios interceptor
            const latestAccess = localStorage.getItem('access_token') || storedAccs[activeIdx]?.access_token
            const latestRefresh = localStorage.getItem('refresh_token') || storedAccs[activeIdx]?.refresh_token

            const matchIdx = storedAccs.findIndex(
              (a) =>
                (fetchedUser?.sso_id && a.user?.sso_id === fetchedUser.sso_id) ||
                (fetchedUser?.id && a.user?.id === fetchedUser.id) ||
                (fetchedUser?.email && a.user?.email === fetchedUser.email)
            )

            let targetIdx = matchIdx
            if (targetIdx >= 0) {
              storedAccs[targetIdx].user = fetchedUser
              storedAccs[targetIdx].access_token = latestAccess
              storedAccs[targetIdx].refresh_token = latestRefresh
            } else {
              if (storedAccs[activeIdx] && (!storedAccs[activeIdx].user || storedAccs[activeIdx].access_token === latestAccess)) {
                targetIdx = activeIdx
                storedAccs[targetIdx].user = fetchedUser
                storedAccs[targetIdx].access_token = latestAccess
                storedAccs[targetIdx].refresh_token = latestRefresh
              } else {
                storedAccs.push({
                  id: fetchedUser?.sso_id || fetchedUser?.id || (fetchedUser?.email ? `email_${fetchedUser.email}` : Date.now()),
                  user: fetchedUser,
                  access_token: latestAccess,
                  refresh_token: latestRefresh,
                })
                targetIdx = storedAccs.length - 1
              }
            }

            // Directly update in-memory user state with fresh data
            setUser(fetchedUser)
            setAccounts([...storedAccs])
            saveStoredAccounts(storedAccs, targetIdx)
            setIsSessionReady(true)
            setAuthError(null)
          } else {
            throw new Error('User data empty')
          }
        } catch (err) {
          setIsSessionReady(false)
          const status = err?.response?.status
          const isExplicitAuthFailure = status === 401 || status === 403
          console.warn('Session recovery warning for active account:', err?.message || err)

          const errorMsg =
            err?.response?.data?.message ||
            (err?.message === 'Network Error' ? 'Unable to reach the server. Please check your connection.' : err?.message) ||
            'Failed to initialize user session.'

          setAuthError({
            title: status === 503 ? 'Database Service Unavailable' : isExplicitAuthFailure ? 'Session Expired' : 'Connection Error',
            message: errorMsg,
            details: err?.response?.data?.error || (err?.code ? `Error code: ${err.code}` : null),
            status: status,
            retryFn: () => {
              resetRefreshCooldown()
              setAuthError(null)
              setIsSessionReady(false)
              isInitializingRef.current = false
              setLoading(true)
              return initialize()
            },
          })

          // Only delete session and clear accounts on explicit auth failure (invalid/expired JWT)
          if (isExplicitAuthFailure) {
            storedAccs.splice(activeIdx, 1)
            if (storedAccs.length > 0) {
              const newIdx = 0
              setAccounts(storedAccs)
              setActiveIndex(newIdx)
              saveStoredAccounts(storedAccs, newIdx)
            } else {
              saveStoredAccounts([], null)
              setAccounts([])
              setActiveIndex(null)
              setSessionExpired(true)
              setShowLoginPopupModal(true)
            }
          } else {
            // For non-JWT errors (e.g. 500, network drop, timeout): preserve the existing session and account
            console.warn('Non-auth error encountered during initialize; session preserved.')
          }
        }
      } finally {
        clearTimeout(safetyTimer)
        setLoading(false)
      }
    }

    initialize()
    return () => {
      cancelled = true
      clearTimeout(safetyTimer)
    }
  }, [saveStoredAccounts])

  const login = async (identity, password) => {
    try {
      setError(null)
      const response = await api.post('/auth/login', { identity, password })
      const access_token = response?.data?.access_token || response?.data?.data?.access_token || null
      const refresh_token = response?.data?.refresh_token || response?.data?.data?.refresh_token || null
      const userData = response?.data?.user || response?.data?.data?.user || null

      if (!access_token || !userData) throw new Error('Invalid login response')

      addOrUpdateAccount(access_token, refresh_token, userData, true)
      setSessionExpired(false)
      setShowLoginPopupModal(false)
      return { accessToken: access_token, refreshToken: refresh_token, user: userData }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Login failed')
      throw err
    }
  }

  const completeBiometricLogin = async (data) => {
    try {
      setError(null)
      const access_token = data?.token || data?.access_token || null
      const refresh_token = data?.refresh_token || null
      const userData = data?.user || null

      if (!access_token || !userData) {
        throw new Error('Invalid biometric login response')
      }

      addOrUpdateAccount(access_token, refresh_token, userData, true)
      setSessionExpired(false)
      setShowLoginPopupModal(false)
      return { accessToken: access_token, refreshToken: refresh_token, user: userData }
    } catch (err) {
      console.error('Biometric completion failed:', err)
      setError(err?.response?.data?.message || err.message || 'Biometric login failed')
      throw err
    }
  }

  const loginWithGoogle = async (idToken) => {
    try {
      setError(null)
      const response = await api.post('/auth/social/google', { id_token: idToken })
      const data = response?.data?.data || response?.data

      if (data?.registration_token) {
        return { action: 'REQUIRE_REGISTRATION', registration_token: data.registration_token, prefill: data.prefill }
      }

      const access_token = data?.access_token || null
      const refresh_token = data?.refresh_token || null
      const userData = data?.user || null

      if (access_token) {
        addOrUpdateAccount(access_token, refresh_token, userData, true)
        setSessionExpired(false)
        setShowLoginPopupModal(false)
        return { accessToken: access_token, refreshToken: refresh_token, user: userData }
      }

      throw new Error('No access token in response')
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Google login failed')
      throw err
    }
  }

  const register = async (formData) => {
    try {
      setError(null)
      const payload = {
        first_name: formData.first_name || formData.firstName,
        last_name: formData.last_name || formData.lastName,
        email: formData.email ? formData.email.trim() : undefined,
        country_code: formData.phone ? (formData.country_code || formData.countryCode) : undefined,
        phone: formData.phone ? formData.phone.trim() : undefined,
        password: formData.password,
        password_confirmation: formData.password_confirmation || formData.passwordConfirmation,
      }

      const res = await api.post('/auth/register', payload)
      const data = res?.data?.data || res?.data

      return {
        user: data?.user || null,
        identities: data?.identities_to_verify || data?.identities || [],
        registration_token: data?.registration_token || null,
        verificationOtps: data?.verification_otps || null,
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Registration failed')
      throw err
    }
  }

  const registerSocial = async (formData) => {
    try {
      setError(null)
      const payload = {
        registration_token: formData.registration_token || formData.registrationToken,
        first_name: formData.first_name || formData.firstName,
        last_name: formData.last_name || formData.lastName,
        email: formData.email ? formData.email.trim() : undefined,
        country_code: formData.phone ? (formData.country_code || formData.countryCode) : undefined,
        phone: formData.phone ? formData.phone.trim() : undefined,
        password: formData.password,
        password_confirmation: formData.password_confirmation || formData.passwordConfirmation,
      }

      const res = await api.post('/auth/register-social', payload)
      const data = res?.data?.data || res?.data

      if (data?.access_token) {
        addOrUpdateAccount(data.access_token, data.refresh_token, data.user, true)
      }

      return {
        accessToken: data?.access_token || null,
        user: data?.user || null,
        requiresVerification: !!data?.identities_to_verify?.length,
        identities: data?.identities_to_verify || [],
        verificationOtps: data?.verification_otps || null,
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Social registration failed')
      throw err
    }
  }

  const switchAccount = useCallback(async (index) => {
    const currentAccounts = loadStoredAccounts()
    if (index === null || index === undefined || index < 0 || index >= currentAccounts.length) {
      return
    }

    // Gate sequential section fetches until target account's /users/me completes
    setIsSessionReady(false)

    const targetAccount = currentAccounts[index]
    const targetToken = targetAccount?.access_token
    const targetRefreshToken = targetAccount?.refresh_token

    // Optimistically switch first so UI feels snappy
    setActiveIndex(index)
    saveStoredAccounts(currentAccounts, index)

    if (targetToken) {
      setAxiosAccessToken(targetToken)
      localStorage.setItem('access_token', targetToken)
    } else {
      localStorage.removeItem('access_token')
    }

    if (targetRefreshToken) {
      localStorage.setItem('refresh_token', targetRefreshToken)
    } else {
      localStorage.removeItem('refresh_token')
    }

    if (targetAccount?.user) {
      setUser(targetAccount.user)
    }

    // Fetch fresh essential user data for the target account explicitly passing targetToken
    try {
      if (targetToken) {
        const res = await api.get('/users/me', {
          headers: { Authorization: `Bearer ${targetToken}` },
        })
        const freshUser = res?.data?.user || res?.data
        if (freshUser) {
          // Update stored account with fresh user data matching target account or freshUser identity.
          // IMPORTANT: Read the current tokens from localStorage rather than using the captured
          // targetToken/targetRefreshToken. If the response interceptor triggered a token refresh
          // during the /users/me call (because the access token was expired), the new tokens are
          // already written to localStorage by setAccessToken(). Using the stale pre-refresh
          // values here would overwrite the rotated tokens via saveStoredAccounts(), causing the
          // very next API request (e.g. /users/me/identity) to be sent with the old expired token,
          // triggering a second redundant refresh that fails because the refresh token was already rotated.
          const latestAccessToken = localStorage.getItem('access_token') || targetToken
          const latestRefreshToken = localStorage.getItem('refresh_token') || targetRefreshToken
          const refreshed = loadStoredAccounts()
          const matchIdx = refreshed.findIndex(
            (a) =>
              (freshUser?.sso_id && a.user?.sso_id === freshUser.sso_id) ||
              (freshUser?.id && a.user?.id === freshUser.id) ||
              (freshUser?.email && a.user?.email === freshUser.email) ||
              (targetAccount?.user?.sso_id && a.user?.sso_id === targetAccount.user.sso_id)
          )
          const updateIdx = matchIdx >= 0 ? matchIdx : index
          if (refreshed[updateIdx]) {
            refreshed[updateIdx] = {
              ...refreshed[updateIdx],
              user: freshUser,
              access_token: latestAccessToken,
              refresh_token: latestRefreshToken || refreshed[updateIdx].refresh_token,
            }
            setAccounts([...refreshed])
            setUser(freshUser)
            saveStoredAccounts(refreshed, updateIdx)
          }
        }
      }
    } catch (err) {
      // Non-fatal — user is still switched; token may be expired (will be caught at next request)
      console.warn('switchAccount: failed to refresh user data:', err?.response?.data?.message || err.message)
    } finally {
      setIsSessionReady(true)
    }
  }, [saveStoredAccounts])

  // Logout only the specified account (or current active account if index is null)
  const logout = async (targetIndex = null) => {
    setIsLoggingOut(true)
    try {
      setError(null)
      let currentAccounts = loadStoredAccounts()
      const idx = targetIndex !== null && targetIndex !== undefined ? targetIndex : getStoredActiveIndex()

      // Determine target tokens to revoke
      let tokenToRevoke = null
      let refreshTokenToRevoke = null

      if (idx !== null && idx >= 0 && idx < currentAccounts.length) {
        const targetAccount = currentAccounts[idx]
        tokenToRevoke = targetAccount?.access_token || null
        refreshTokenToRevoke = targetAccount?.refresh_token || null
      }

      // Fallback to active state / localStorage if target not found in storedAccounts
      if (!tokenToRevoke && !refreshTokenToRevoke) {
        tokenToRevoke = accessToken || localStorage.getItem('access_token') || null
        refreshTokenToRevoke = refreshToken || localStorage.getItem('refresh_token') || null
      }

      // ALWAYS dispatch backend logout to revoke the session & invalidate token
      // Wait for backend to fully complete BEFORE modifying client state
      try {
        const headers = tokenToRevoke ? { Authorization: `Bearer ${tokenToRevoke}` } : {}
        await api.post(
          '/auth/logout',
          {
            refresh_token: refreshTokenToRevoke || null,
            access_token: tokenToRevoke || null,
          },
          { headers }
        )
      } catch (backendErr) {
        console.warn('Backend logout request encountered an error (continuing client cleanup):', backendErr?.message || backendErr)
      }

      // ONLY after backend logout finishes, apply client state transitions
      if (idx === null || idx < 0 || idx >= currentAccounts.length) {
        saveStoredAccounts([], null)
        setAccounts([])
        setActiveIndex(null)
        setSessionExpired(false)
        setShowLoginPopupModal(false)
        return { remainingCount: 0, newActiveIndex: null }
      }

      // Remove only the target account
      currentAccounts = currentAccounts.filter((_, i) => i !== idx)

      let newActiveIndex = null
      if (currentAccounts.length > 0) {
        newActiveIndex = idx < currentAccounts.length ? idx : Math.max(0, currentAccounts.length - 1)
      }

      setAccounts(currentAccounts)
      setActiveIndex(newActiveIndex)
      saveStoredAccounts(currentAccounts, newActiveIndex)

      setSessionExpired(false)
      setShowLoginPopupModal(false)

      return { remainingCount: currentAccounts.length, newActiveIndex }
    } catch (err) {
      setError(err.message || 'Logout failed')
      throw err
    } finally {
      setIsLoggingOut(false)
    }
  }

  // Log out all stored accounts simultaneously
  const logoutAll = async () => {
    setIsLoggingOut(true)
    try {
      setError(null)
      const currentAccounts = loadStoredAccounts()

      const accountsToRevoke = [...currentAccounts]
      if (accountsToRevoke.length === 0 && (accessToken || localStorage.getItem('access_token') || localStorage.getItem('refresh_token'))) {
        accountsToRevoke.push({
          access_token: accessToken || localStorage.getItem('access_token'),
          refresh_token: refreshToken || localStorage.getItem('refresh_token'),
        })
      }

      // Wait for all backend logout requests to complete before client-side clearing
      await Promise.all(
        accountsToRevoke.map(async (acc) => {
          try {
            const headers = acc?.access_token ? { Authorization: `Bearer ${acc.access_token}` } : {}
            await api.post(
              '/auth/logout',
              {
                refresh_token: acc?.refresh_token || null,
                access_token: acc?.access_token || null,
                all: true,
                all_devices: true,
              },
              { headers }
            )
          } catch (err) {
            console.warn('Backend logoutAll error for account:', err?.message || err)
          }
        })
      )

      // Clear all accounts & tokens from localStorage only after backend responds
      saveStoredAccounts([], null)
      localStorage.removeItem(STORAGE_KEY_ACCOUNTS)
      localStorage.removeItem(STORAGE_KEY_ACTIVE_INDEX)

      setAccounts([])
      setActiveIndex(null)
      setSessionExpired(false)
      setShowLoginPopupModal(false)
    } catch (err) {
      setError(err.message || 'LogoutAll failed')
    } finally {
      setIsLoggingOut(false)
    }
  }


  const addAccount = async (credentials) => {
    return login(credentials.identity || credentials.email || credentials.username, credentials.password)
  }

  const getActiveUser = () => {
    if (user) return user
    const list = accounts.length > 0 ? accounts : loadStoredAccounts()
    const idx = activeIndex !== null ? activeIndex : getStoredActiveIndex()
    if (idx !== null && list[idx]) return list[idx].user
    try {
      const u = localStorage.getItem('user')
      return u ? JSON.parse(u) : null
    } catch (_) {
      return null
    }
  }

  const getAccountsList = () => {
    const list = accounts.length > 0 ? accounts : loadStoredAccounts()
    const currentActive = activeIndex !== null ? activeIndex : getStoredActiveIndex()
    return list.map((a, i) => ({
      index: i,
      user: a.user,
      active: i === currentActive,
      access_token: a.access_token,
      refresh_token: a.refresh_token,
    }))
  }

  const verifyOtp = async (registrationToken, identityValue, otp) => {
    try {
      setError(null)
      const response = await api.post('/auth/verify-otp', { registration_token: registrationToken, identity_value: identityValue, otp })
      const access = response.data?.access_token || null
      const refresh_tok = response.data?.refresh_token || null
      const userData = response.data?.user || null
      if (access && userData) {
        addOrUpdateAccount(access, refresh_tok, userData, true)
      }
      return response.data
    } catch (err) {
      setError(err.response?.data?.message || 'OTP verification failed')
      throw err
    }
  }

  const resendOtp = async (identityValue) => {
    try {
      setError(null)
      const response = await api.post('/auth/mfa/request-otp', { identity_value: identityValue })
      return response?.data?.data || response?.data
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP')
      throw err
    }
  }

  const refreshCurrentUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token') || accessToken
      if (!token) return null

      const res = await api.get('/users/me', {
        headers: { Authorization: `Bearer ${token}` }
      })
      const freshUser = res?.data?.user || res?.data
      if (freshUser) {
        setUser(freshUser)
        const currentAccounts = loadStoredAccounts()
        const matchIdx = currentAccounts.findIndex(
          (a) =>
            (freshUser?.sso_id && a.user?.sso_id === freshUser.sso_id) ||
            (freshUser?.id && a.user?.id === freshUser.id) ||
            (freshUser?.email && a.user?.email === freshUser.email)
        )
        const targetIdx = matchIdx !== -1 ? matchIdx : (getStoredActiveIndex() ?? 0)
        if (currentAccounts[targetIdx]) {
          currentAccounts[targetIdx].user = freshUser
          setAccounts([...currentAccounts])
          saveStoredAccounts(currentAccounts, targetIdx)
        }
        return freshUser
      }
    } catch (err) {
      console.warn('refreshCurrentUser failed:', err?.message || err)
    }
    return null
  }, [accessToken, saveStoredAccounts])

  const isAuthenticated = !!(accessToken || localStorage.getItem('access_token'))

  return (
    <AuthContext.Provider
      value={{
        loading,
        isLoggingOut,
        error,
        user,
        accessToken,
        refreshToken,
        isAuthenticated,
        setSession,
        login,
        loginWithGoogle,
        completeBiometricLogin,
        register,
        registerSocial,
        logout,
        logoutAll,
        addAccount,
        switchAccount,
        getActiveUser,
        getAccountsList,
        refreshCurrentUser,
        verifyOtp,
        resendOtp,
        accounts,
        activeIndex,
        sessionExpired,
        showLoginPopupModal,
        setShowLoginPopupModal,
        authError,
        setAuthError,
        clearAuthError: () => setAuthError(null),
        isSessionReady,
        setNavigateCallback: (fn) => { navigateRef.current = fn },
        dismissSessionExpired: () => {
          setSessionExpired(false)
          setShowLoginPopupModal(false)
        }
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
